import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ChevronLeft, ChevronRight, ArrowLeft, CalendarDays } from "lucide-react";
import { Logo } from "@/components/Logo";
import { EVENT_TYPES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ["Ιανουάριος", "Φεβρουάριος", "Μάρτιος", "Απρίλιος", "Μάιος", "Ιούνιος", "Ιούλιος", "Αύγουστος", "Σεπτέμβριος", "Οκτώβριος", "Νοέμβριος", "Δεκέμβριος"];
const WEEKDAYS = ["Δε", "Τρ", "Τε", "Πε", "Πα", "Σα", "Κυ"];

const ymd = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const typeColor = (t) => EVENT_TYPES[t] || "#6B7280";

export default function Calendar() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/events`).then(({ data }) => setEvents(data)).catch(() => {});
  }, []);

  const byDate = useMemo(() => {
    const map = {};
    events.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [events]);

  const cells = useMemo(() => {
    const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const arr = Array(firstWeekday).fill(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(d);
    return arr;
  }, [year, month]);

  const upcoming = useMemo(() => {
    const todayStr = ymd(now.getFullYear(), now.getMonth(), now.getDate());
    return [...events].filter((e) => e.date >= todayStr).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 6);
  }, [events]);

  const changeMonth = (delta) => {
    let m = month + delta, y = year;
    if (m < 0) { m = 11; y--; } else if (m > 11) { m = 0; y++; }
    setMonth(m); setYear(y); setSelected(null);
  };

  const todayStr = ymd(now.getFullYear(), now.getMonth(), now.getDate());
  const selectedEvents = selected ? byDate[selected] || [] : [];

  return (
    <div className="min-h-screen bg-background font-body text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-black/10">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="calendar-logo">
            <Logo className="h-10 w-12 transition-transform group-hover:rotate-[360deg] duration-700" ringColor="#0A1A4A" />
            <div className="leading-none">
              <div className="font-heading font-extrabold text-lg">Φυσική Επιλογή</div>
              <div className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Ημερολόγιο</div>
            </div>
          </Link>
          <Link to="/" data-testid="calendar-back" className="inline-flex items-center gap-2 font-mono-accent text-xs uppercase tracking-widest text-foreground/70 hover:text-primary transition-colors">
            <ArrowLeft className="h-4 w-4" /> Αρχική
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-3">// Ημερολόγιο Εκδηλώσεων</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight flex items-center gap-3">
            <CalendarDays className="h-8 w-8 text-primary" strokeWidth={1.5} /> Πρόγραμμα & Εκδηλώσεις
          </h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold" data-testid="calendar-month-label">{MONTHS[month]} {year}</h2>
              <div className="flex items-center gap-2">
                <button onClick={() => changeMonth(-1)} data-testid="calendar-prev" className="h-10 w-10 flex items-center justify-center border border-border rounded-sm hover:border-primary hover:text-primary transition-colors"><ChevronLeft className="h-5 w-5" /></button>
                <button onClick={() => changeMonth(1)} data-testid="calendar-next" className="h-10 w-10 flex items-center justify-center border border-border rounded-sm hover:border-primary hover:text-primary transition-colors"><ChevronRight className="h-5 w-5" /></button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-px bg-border border border-border rounded-sm overflow-hidden">
              {WEEKDAYS.map((w) => (
                <div key={w} className="bg-foreground text-white font-mono-accent text-[10px] uppercase tracking-widest py-2.5 text-center">{w}</div>
              ))}
              {cells.map((d, i) => {
                if (!d) return <div key={`e${i}`} className="bg-secondary/40 min-h-[92px]" />;
                const ds = ymd(year, month, d);
                const dayEvents = byDate[ds] || [];
                const isToday = ds === todayStr;
                return (
                  <button
                    key={ds}
                    data-testid={`calendar-day-${ds}`}
                    onClick={() => setSelected(ds)}
                    className={`bg-white min-h-[92px] p-2 text-left align-top hover:bg-secondary/60 transition-colors ${selected === ds ? "ring-2 ring-inset ring-primary" : ""}`}
                  >
                    <span className={`inline-flex items-center justify-center text-xs font-mono-accent h-6 w-6 rounded-full ${isToday ? "bg-accent text-white" : "text-muted-foreground"}`}>{d}</span>
                    <div className="mt-1 space-y-1">
                      {dayEvents.slice(0, 3).map((ev) => (
                        <div key={ev.id} className="flex items-center gap-1.5 min-w-0">
                          <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: typeColor(ev.type) }} />
                          <span className="text-[11px] truncate leading-tight">{ev.title}</span>
                        </div>
                      ))}
                      {dayEvents.length > 3 && <span className="text-[10px] text-muted-foreground">+{dayEvents.length - 3} ακόμη</span>}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-4 mt-5">
              {Object.entries(EVENT_TYPES).map(([name, color]) => (
                <div key={name} className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
                  <span className="text-xs text-muted-foreground">{name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Side panel */}
          <div className="lg:col-span-1 space-y-8">
            {selected && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="calendar-selected" className="bg-white border border-border rounded-sm p-5">
                <div className="font-mono-accent text-[10px] uppercase tracking-widest text-accent mb-3">Επιλεγμένη ημέρα</div>
                <div className="font-heading font-bold text-lg mb-3">{selected.split("-").reverse().join("/")}</div>
                {selectedEvents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Καμία εκδήλωση.</p>
                ) : (
                  <ul className="space-y-3">
                    {selectedEvents.map((ev) => (
                      <li key={ev.id} className="border-l-2 pl-3" style={{ borderColor: typeColor(ev.type) }}>
                        <div className="font-medium text-sm">{ev.title}</div>
                        <div className="text-xs text-muted-foreground">{ev.type}{ev.note ? ` · ${ev.note}` : ""}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            )}

            <div>
              <h3 className="font-heading font-bold text-lg mb-4">Επόμενες εκδηλώσεις</h3>
              <div className="space-y-3" data-testid="calendar-upcoming">
                {upcoming.length === 0 && <p className="text-sm text-muted-foreground">Δεν υπάρχουν προγραμματισμένες εκδηλώσεις.</p>}
                {upcoming.map((ev) => {
                  const [y, m, d] = ev.date.split("-");
                  return (
                    <div key={ev.id} className="flex gap-4 bg-white border border-border rounded-sm p-3">
                      <div className="text-center shrink-0 w-12">
                        <div className="font-heading text-xl font-extrabold leading-none">{d}</div>
                        <div className="font-mono-accent text-[10px] uppercase text-muted-foreground mt-1">{MONTHS[parseInt(m, 10) - 1].slice(0, 3)}</div>
                      </div>
                      <div className="min-w-0 border-l-2 pl-3" style={{ borderColor: typeColor(ev.type) }}>
                        <div className="font-medium text-sm">{ev.title}</div>
                        <div className="text-xs text-muted-foreground">{ev.type}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
